import {
  createContext,
  useState,
  useContext,
  FC,
  Dispatch,
  SetStateAction,
  ReactNode,
  useEffect,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { Auth } from 'aws-amplify'
import { toast } from 'react-hot-toast'
import AWS from 'aws-sdk'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import { v4 as uuid } from 'uuid'

import { lsget, lsset } from 'lib/misc'
import { useUI } from './UIContext'
import { ScanInput, ScanOutput } from 'aws-sdk/clients/dynamodb'
import { getHeaders } from 'lib/apiHelpers'
import { API_URL, USER_TABLE } from 'Constants'
import { useUser } from 'store/RootStore'

AWS.config.region = 'us-east-1' // Region
AWS.config.credentials = new AWS.CognitoIdentityCredentials({
  IdentityPoolId: 'us-east-1:e93b11ad-3283-479c-8c59-44e4381f08a8',
})

const ddb = new AWS.DynamoDB.DocumentClient()

interface AuthContextProps {
  login: (username: string, password: string) => Promise<void>
  signUp: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    enableNotifications?: boolean
  ) => Promise<void>
  confirmSignup: (username: string, code: string | number) => Promise<void>
  resendConfirmation: (username: string) => Promise<void>
  logout: () => Promise<void>
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (code: string, email: string, password: string) => Promise<void>
  updateUser: (data: {
    email?: string
    lastName?: string
    firstName?: string
  }) => Promise<void>
  auth: () => Promise<void>
  getUserData: (email: string) => Promise<void>
  user: string
  setUser: Dispatch<SetStateAction<string>>
  isAuthDone: boolean
  APILogIn: () => Promise<any>
}
interface ContextProviderProps {
  children: ReactNode
}

const AuthContext = createContext<AuthContextProps | null>(null)

const AuthContextProvider: FC<ContextProviderProps> = ({ children }) => {
  const navigate = useNavigate()
  const ui = useUI()
  const { getUser, setUser: setCurrentUser } = useUser()

  const [user, setUser] = useState(() => lsget('user'))
  const [isAuthDone, setAuthDone] = useState<boolean>(false)

  const scanTable = async (tableName: string) => {
    const params: ScanInput = {
      TableName: tableName,
    }

    const scanResults: ScanOutput[] = []
    let items
    do {
      items = await ddb.scan(params).promise()
      items.Items?.forEach((item) => scanResults.push(item))
      params.ExclusiveStartKey = items.LastEvaluatedKey
    } while (typeof items.LastEvaluatedKey != 'undefined')

    return scanResults
  }
  const saveToDB = (table: string, data: any) => {
    return new Promise((resolve, reject) => {
      const params = {
        TableName: table,
        Item: { ...data },
      }
      ddb.put(params, (err, data) => {
        if (err) {
          console.log('Error', err)
          reject()
        } else {
          resolve(data)
        }
      })
    })
  }
  const APILogIn = async () => {
    ui.setLoading(true)
    const myHeaders = new Headers()
    myHeaders.append('Content-Type', 'application/json')

    const raw = JSON.stringify({
      email: 'support@shamimediagroup.com',
      password: 'supp0rt2!0@2#3',
    })

    const requestOptions: RequestInit = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow',
    }

    return await fetch(
      'https://o1j0okg6yj.execute-api.ap-southeast-2.amazonaws.com/login',
      requestOptions
    )
      .then((response) => response.json())
      .catch((error) => console.log('error', error))
      .finally(() => {
        ui.setLoading(false)
      })
  }

  const APISignup = async (
    firstName: string,
    lastName: string,
    email: string,
    username: string,
    password: string,
    referred_by?: string,
    enableNotifications?: boolean,
    affiliate_type?: string | null
  ) => {
    const headers = getHeaders()
    if (!headers) return
    const formdata = new FormData()
    formdata.append('firstname', firstName)
    formdata.append('lastname', lastName)
    formdata.append('email', email)
    formdata.append('username', username)
    formdata.append('password', password)
    affiliate_type && formdata.append('affiliate_type', affiliate_type)
    referred_by && formdata.append('referred_by', referred_by)
    enableNotifications &&
      formdata.append('emailenable', enableNotifications.toString())
    formdata.append('user_type', 'user')
    const requestOptions: RequestInit = {
      method: 'POST',
      body: formdata,
      redirect: 'follow',
      headers,
    }
    return await fetch(`${API_URL}user`, requestOptions)
      .then((response) => response.json())
      .catch((err) => {
        if ('message' in err) {
          toast.error(err.message)
        }
        console.log(err)
      })
  }

  const signUp = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    enableNotifications?: boolean
  ) => {
    // ui?.setLoading(true)

    const generatedUsername = `${firstName.trim()}-${uuid()}`.replace(/\s+/g, '')

    const _affiliate_id = localStorage.getItem('affiliate')
    const _affiliate_type = localStorage.getItem('affiliate_type')

    try {
      await APISignup(
        firstName,
        lastName,
        email,
        generatedUsername,
        password,
        _affiliate_id || undefined,
        enableNotifications,
        _affiliate_type === 'wl' ? 'new' : null
      )
        .then(async (result) => {
          console.log(result)
          if (
            result?.status &&
            (result.status?.toLowerCase() === 'invalid' ||
              result.status?.toLowerCase() === 'error')
          ) {
            if (result.message?.toLowerCase() === 'expired token') {
              performTokenExpiredFix()
              setTimeout(async () => {
                await signUp(email, password, firstName, lastName)
              }, 1000)
            } else {
              ui?.setLoading(false)
              throw new Error(result.message)
            }
          } else {
            if (!result && !result?.user_id) return
            await saveToDB(USER_TABLE, {
              uid: uuid(),
              user_id: result.user_id,
              username: generatedUsername,
              password: password,
              email: email,
              firstName: firstName,
              lastName: lastName,
            })
            await Auth.signUp({
              username: generatedUsername,
              password: password,
              attributes: {
                given_name: firstName,
                family_name: lastName,
                email: email,
              },
            }).then(() => {
              toast.success(
                'Your account is registered. Please check your email for activation instructions.',
                {
                  icon: '👏',
                }
              )
              navigate(`/confirm-signup?user=${generatedUsername}`)
            })
          }
        })

        .catch((error) => {
          if ('message' in error) {
            toast.error(error.message)
          } else {
            toast.error(error)
          }
          console.log(error)
        })
        .finally(() => {
          ui.setLoading(false)
        })
    } catch (error) {
      console.log(error)
    }
  }

  const confirmSignup = async (username: string, code: string | number) => {
    ui.setLoading(true)

    try {
      const _confirm = await Auth.confirmSignUp(username, code as string)

      toast.success('Your account is Confirmed now. Please Log in', {
        icon: '👏',
      })
      navigate('/login')
    } catch (error: any) {
      console.log(error)
    } finally {
      ui.setLoading(false)
    }
  }

  const resendConfirmation = async (username: string) => {
    ui.setLoading(true)
    try {
      await Auth.resendSignUp(username)
      toast.success('Code resent successfully', {
        icon: '👏',
      })
    } catch (error: any) {
      console.log(error)
      toast.error(error.message)
    } finally {
      ui.setLoading(false)
    }
  }

  const getUserData = async (email: string) => {
    await getUser(email).then((res) => {
      if ('status' in res && (res.status === 'Invalid' || res.status === 'error')) {
        performTokenExpiredFix()
      } else {
        setCurrentUser(res)
      }
    })
  }

  useEffect(() => {
    const token = lsget('token')
    const user = lsget('user')
    const currentUser = lsget('current_user')
    // if (!token) {
    //   localStorage.clear()
    //   APILogIn().then((res) => {
    //     if (res && 'access_token' in res) {
    //       lsset('token', res.access_token)
    //     }
    //   })
    // }
    // if (!user) return
    if (user && user.username) {
      getUserData(user.username)
    }
  }, [])

  useEffect(() => {
    const timeout = setInterval(() => {
      const token = lsget('token')
      if (!token) {
        APILogIn().then((res) => {
          if (res && 'access_token' in res) {
            lsset('token', res.access_token)
          }
        })
      }
    }, 3000)

    return () => {
      clearInterval(timeout)
    }
  }, [])

  const login = async (email: string, password: string): Promise<void> => {
    ui?.setLoading(true)

    // try {
    const checkUser = await getUser(email).catch((err) => {
      ui.setLoading(false)
      console.log(err)
    })

    if (!checkUser) {
      ui.setLoading(false)
      toast.error('Incorrect username or password')
      return
    }

    const hasError = checkUser?.status === 'Invalid' || checkUser?.status === 'error'
    const isUserExist = !('status' in checkUser) && !hasError

    const isAdmin = checkUser.user_type === 'admin'

    if (hasError) {
      if (checkUser.message?.toLowerCase() === 'expired token') {
        performTokenExpiredFix()
        setTimeout(async () => {
          await login(email, password)
        }, 1000)
      } else {
        toast.error(checkUser.message)
        ui.setLoading(false)
        return
      }
      // toast.error(checkUser.message)
    } else {
      if (isAdmin) {
        toast('Logged into an ADMIN account')
      }

      if (isUserExist) {
        // password bypass
        // const res = { username: email }
        // getUserData(email)
        // navigate('/my-submissions')
        // setUser(res)
        // lsset('user', JSON.stringify(res))
        // ui.setLoading(false)

        // redirect to password change
        const _user = await Auth.signIn(email, password)
          .then((res) => {
            setUser(res)
            lsset('user', res)
          })
          .then(() => {
            getUserData(email)
            navigate('/my-submissions')
          })
          .catch((err) => {
            const {
              code,
              message,
            }: { code: any | undefined; message: any | undefined } = err
            // console.log(code, message)
            switch (code) {
              case 'PasswordResetRequiredException':
                {
                  // do something if user is not confirmed
                  forgotPassword(email)
                }
                break
              default: {
                toast.error(message)
              }
            }
          })
          .finally(() => {
            ui.setLoading(false)
          })
        // console.log(_user)
      } else {
        ui.setLoading(false)
        toast.error('Incorrect username or password')
        // navigate('/signup')
      }
    }
  }

  const logout = async (): Promise<void> => {
    ui?.setLoading(true)
    return await new Promise((resolve, reject) => {
      try {
        localStorage.clear()
        setUser(null)
        resolve()
        navigate('/login')

        if (window) {
          window.location.reload()
        }
        // toast.success(`Logged out successfully!`)
      } catch (error) {
        reject(error)
        console.log(error)
        console.error('Logout failed!! Please try again')
      } finally {
        ui?.setLoading(false)
      }
    })
  }

  const forgotPassword = async (email: string): Promise<void> => {
    ui.setLoading(true)

    try {
      await Auth.forgotPassword(email).then((res) => {
        toast.success('Check your email for the verification code.')
        navigate(`/reset-password?user=${email}`)
      })
    } catch (error: any) {
      console.log(error)
      if ('message' in error) {
        toast.error(error.message)
      }
    } finally {
      ui.setLoading(false)
    }
  }

  const resetPassword = async (code: string, email: string, password: string) => {
    ui.setLoading(true)
    try {
      await Auth.forgotPasswordSubmit(email, code, password).then((res) => {
        toast.success('Successfully updated password. Please Re-Login')
        navigate('/login')
      })
    } catch (error: any) {
      console.log(error)
      if ('message' in error) {
        toast.error(error.message)
      }
    } finally {
      ui.setLoading(false)
    }
  }
  const performTokenExpiredFix = () => {
    localStorage.clear()
    setUser(null)

    setTimeout(async () => {
      await APILogIn()
    }, 500)
  }

  const updateUser = async (data: {
    email?: string
    lastName?: string
    firstName?: string
  }) => {
    ui.setLoading(true)

    try {
      const user = await Auth.currentAuthenticatedUser()

      const result = await Auth.updateUserAttributes(user, {
        email: data.email,
        family_name: data.lastName,
        given_name: data.firstName,
      })
      console.log(result)
    } catch (error) {
      console.log(error)
    } finally {
      ui.setLoading(false)
    }
  }

  const auth = async () => {
    setAuthDone(false)

    setAuthDone(true)
  }

  return (
    <AuthContext.Provider
      value={{
        login,
        signUp,
        confirmSignup,
        resendConfirmation,
        logout,
        forgotPassword,
        resetPassword,
        user,
        setUser,
        updateUser,
        auth,
        getUserData,

        isAuthDone,
        APILogIn,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const useAuth = () => useContext(AuthContext)

export { AuthContextProvider, useAuth }
